import axios, { type AxiosResponse } from 'axios';
import { setSubmitFailed, setSubmitSucceeded, startSubmit, stopSubmit } from 'redux-form';
import {
  call,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
  type ForkEffect,
} from 'redux-saga/effects';

import * as automationsActions from '~/actions/automations.actions';
import { ActionKeys } from '~/actions/constants';
import { AUTOMATIONS_RULE_FORM } from '~/constants/form';
import { AUTOMATION_CREATED, FIRST_AUTOMATION_CREATED } from '~/constants/gtm';
import { notificationCloseTimeout } from '~/constants/notifications';
import {
  reprocessPollingDelay,
  reprocessPollingTimeout,
  ruleReprocessingStatus,
} from '~/constants/rules';
import { fetchServiceLevelsByAccount } from '~/sagas/providers/providers.saga';
import { hasAutomations } from '~/selectors/automations.selectors';
import * as automationsService from '~/services/automations.service';
import { sendGTMEvent } from '~/services/gtm.service';
import * as AutomationRule from '~/types/AutomationRule';
import * as AutomationRuleError from '~/types/AutomationRuleError';
import { type JSONBusinessRule } from '~/types/BusinessRule';
import {
  generateReorderRulesPayload,
  parseBusinessRules,
  ruleReprocessingCompleteOrErrored,
} from '~/utils/businessRules';

export function* fetchBusinessRules() {
  try {
    const getResponse: AxiosResponse<JSONBusinessRule[]> = yield call(
      automationsService.getBusinessRules,
    );
    const payload = parseBusinessRules(getResponse.data);
    yield put(automationsActions.fetchBusinessRulesSuccess(payload));
  } catch (err) {
    const payload = {
      errorMessage: (err as Error).message,
    };
    yield put(automationsActions.fetchBusinessRulesError(payload));
  }
}

function* startRuleReprocessing() {
  try {
    yield call(automationsService.reprocessRules);
    yield put(automationsActions.resetReprocessingPendingNotification());
    yield put(automationsActions.resetReprocessingCompleteNotification());
    yield put(automationsActions.startRuleReprocessStatusPolling());
  } catch (err) {
    // we would see an error such as 400 if the user somehow requests reprocessing while existing
    // process is running. On the FE we disable the button to trigger it, so that shouldn't happen.
  }
}

function* getRuleReprocessingStatus() {
  try {
    const result: AxiosResponse<{ status: string }> = yield call(
      automationsService.getRuleReprocessStatus,
    );
    const status = result?.data?.status;

    // if the first get call is complete, error, or nil then don't show notif
    if (!ruleReprocessingCompleteOrErrored(status))
      yield put(automationsActions.startRuleReprocessStatusPolling());
  } catch (err) {
    // Continue with error.
  }
}

function* deleteBusinessRule(action: ReturnType<typeof automationsActions.deleteRule>) {
  try {
    yield call(automationsService.deleteRule, action.payload.ruleId);
    yield put(automationsActions.deleteRuleSuccess());
    yield put(automationsActions.fetchBusinessRules());
  } catch (err) {
    yield put(automationsActions.deleteRuleError((err as Error).message));
  }
}

function* createBusinessRule(action: ReturnType<typeof automationsActions.createRule>) {
  try {
    yield put(startSubmit(AUTOMATIONS_RULE_FORM));
    const payload = AutomationRule.toJSON(action.payload.ruleFormData);

    if (payload && payload.object_id) yield call(automationsService.updateRule, payload);
    else yield call(automationsService.createRule, payload);

    yield put(stopSubmit(AUTOMATIONS_RULE_FORM));
    yield put(automationsActions.createRuleSuccess());
    yield put(setSubmitSucceeded(AUTOMATIONS_RULE_FORM));
    yield put(automationsActions.closeAddRuleModal());
    yield put(automationsActions.setRuleError(AutomationRuleError.EMPTY_RULE_ERROR));
    // update the Service Levels by Account to reflect which service levels are marked "is_used" (by an automation rule)
    yield call(fetchServiceLevelsByAccount);
    yield put(automationsActions.fetchBusinessRules());
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { response } = err as any;

    if (!response && axios.isAxiosError(err))
      yield put(automationsActions.setRuleError({ kind: 'GENERIC_ERROR' }));

    // ensure that we only set an error if it's a response error
    // and not an error in the js logic in the success handler
    if (response) {
      const error = AutomationRuleError.fromJSON(response);

      yield put(automationsActions.setRuleError(error));
      yield put(stopSubmit(AUTOMATIONS_RULE_FORM));
      yield put(setSubmitFailed(AUTOMATIONS_RULE_FORM));
    }
  }
}

function* reorderRules(action: ReturnType<typeof automationsActions.reorderRules>) {
  try {
    const payload = {
      rules: generateReorderRulesPayload(action.payload.rules),
    };
    yield call(automationsService.reorderRules, payload);
    yield put(automationsActions.reorderRulesSuccess(action.payload.rules));
  } catch (err) {
    yield put(automationsActions.reorderRulesError((err as Error).message));
  }
}

function* autoCloseRuleReprocessingCompleteNotification(
  action: ReturnType<typeof automationsActions.updateRuleReprocessStatus>,
) {
  try {
    const newStatus = action.payload.status;
    if (ruleReprocessingCompleteOrErrored(newStatus)) {
      yield delay(notificationCloseTimeout); // 6s timeout for automatically closing the complete notification
      yield put(automationsActions.closeReprocessingCompleteNotification());
    }
  } catch (err) {
    // Continue with error.
  }
}

// rule reprocessing worker
let worker: ForkEffect | null = null;

// worker that gets called by the method to start polling
function* ruleReprocessStatusWorker() {
  while (true) {
    try {
      const result: AxiosResponse<{ status: string }> = yield call(
        automationsService.getRuleReprocessStatus,
      );
      const status = result?.data?.status;

      // always save rule status
      yield put(automationsActions.updateRuleReprocessStatus(status || ''));
      if (!status) {
        // status is nil if no recent reprocess request
        yield put(automationsActions.stopRuleReprocessStatusPolling());
      } else if (ruleReprocessingCompleteOrErrored(status)) {
        yield put(automationsActions.stopRuleReprocessStatusPolling());
      } else {
        yield delay(reprocessPollingDelay); // 1.5s delay between status checks
      }
    } catch (err) {
      yield put(automationsActions.updateRuleReprocessStatus(ruleReprocessingStatus.error));
      yield put(automationsActions.stopRuleReprocessStatusPolling());
    }
  }
}

function* ruleReprocessStatusPollRace() {
  const result: { timeout: boolean } = yield race({
    cancel: take([ActionKeys.AUTOMATIONS_REPROCESS_RULES_POLL_END]),
    task: call(ruleReprocessStatusWorker),
    timeout: delay(reprocessPollingTimeout, true), // 60s timeout
  });

  if (result.timeout) {
    yield put(automationsActions.updateRuleReprocessStatus(ruleReprocessingStatus.error));
    yield put(automationsActions.stopRuleReprocessStatusPolling());
  }

  worker = null;
}

// starts polling for rule reprocess status
function* ruleReprocessStatusPollWatcher() {
  while (true) {
    yield take([ActionKeys.AUTOMATIONS_REPROCESS_RULES_POLL_START]);

    if (!worker) worker = yield fork(ruleReprocessStatusPollRace);
  }
}

function* sendGTMEventOnRuleCreation() {
  const userHasAutomations = yield select(hasAutomations);
  if (!userHasAutomations) yield call(sendGTMEvent, { eventName: FIRST_AUTOMATION_CREATED });
  yield call(sendGTMEvent, { eventName: AUTOMATION_CREATED });
}

export default [
  takeLatest(ActionKeys.FETCH_BUSINESS_RULES, fetchBusinessRules),
  takeLatest(ActionKeys.AUTOMATIONS_DELETE_RULE, deleteBusinessRule),
  takeLatest(ActionKeys.AUTOMATIONS_CREATE_RULE, createBusinessRule),
  takeLatest(ActionKeys.AUTOMATIONS_REORDER_RULES, reorderRules),
  takeLatest(ActionKeys.AUTOMATIONS_REPROCESS_RULES, startRuleReprocessing),
  takeLatest(ActionKeys.AUTOMATIONS_GET_REPROCESS_STATUS, getRuleReprocessingStatus),
  takeLatest(ActionKeys.AUTOMATIONS_CREATE_RULE_SUCCESS, sendGTMEventOnRuleCreation),
  takeLatest(
    ActionKeys.AUTOMATIONS_UPDATE_REPROCESS_STATUS,
    autoCloseRuleReprocessingCompleteNotification,
  ),
  ruleReprocessStatusPollWatcher(),
];
